import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@repo/ui";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { z } from "zod";

interface RegistrationItem {
  id: number;
  name: string;
  surname: string;
  phone: string;
  email: string;
  age: number | null;
  interests: string[];
  interest_other?: string | null;
  banks: string[];
  property_bought?: string | null;
  value_amount?: string | null;
  developer?: string | null;
  address?: string | null;
}

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  surname: z.string().min(1, "Surname is required"),
  phone: z.string().min(1, "Phone is required"),
  email: z.string().email("Valid email is required"),
  age: z.number().nullable(),
  property_bought: z.string().min(1, "Property is required"),
  value_amount: z.string().min(1, "Value is required"),
  developer: z.string().min(1, "Developer is required"),
  address: z.string().min(1, "Address is required"),
});

type FormValues = z.infer<typeof formSchema>;

export default function CouponPage() {
  const params = useParams();
  const [search] = useSearchParams();
  const id = params.id || search.get("id") || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [item, setItem] = useState<RegistrationItem | null>(null);
  const [saved, setSaved] = useState(false);
  const [valueRaw, setValueRaw] = useState("");

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      surname: "",
      phone: "",
      email: "",
      age: null,
      property_bought: "",
      value_amount: "",
      developer: "",
      address: "",
    },
  });

  useEffect(() => {
    const run = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const res = await fetch(`/api/registrations/${id}`);
        if (!res.ok) throw new Error("Not found");
        const data = (await res.json()) as { item: RegistrationItem };
        setItem(data.item);
        const initial = (data.item.value_amount ?? "").replace(/\D/g, "");
        setValueRaw(initial);

        form.reset({
          name: data.item.name,
          surname: data.item.surname,
          phone: data.item.phone,
          email: data.item.email,
          age: data.item.age,
          property_bought: data.item.property_bought ?? "",
          value_amount: data.item.value_amount ?? "",
          developer: data.item.developer ?? "",
          address: data.item.address ?? "",
        });
      } catch {
        setError("ไม่พบข้อมูล / Not found");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id, form]);

  const onSubmit = async (values: FormValues) => {
    if (!item) return;
    setSaving(true);
    try {
      await fetch(`/api/registrations/${item.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: values.name,
          surname: values.surname,
          phone: values.phone,
          email: values.email,
          age: values.age,
          interests: item.interests,
          interestOther: item.interest_other ?? null,
          banks: item.banks,
          property_bought: values.property_bought,
          value_amount: values.value_amount,
          developer: values.developer,
          address: values.address,
        }),
      });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-2xl text-center">
            {saved
              ? "ลงทะเบียนคูปองสำเร็จ / Coupon registration complete"
              : "ตอบคำถามเพิ่มเพื่อรับ Coupon / Answer to get a Coupon"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div>Loading...</div>
          ) : error ? (
            <div className="text-red-600 text-sm">{error}</div>
          ) : saved && item ? (
            <div className="space-y-5">
              <p className="text-center text-sm text-muted-foreground">
                กรุณาแสดงให้เจ้าหน้าที่ดู / Please show this to a staff member
              </p>
              <div className="flex flex-col items-center gap-3">
                <img
                  alt="QR Code"
                  className="size-48"
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(String(item.id))}`}
                />
                <div className="text-sm">ID: {item.id}</div>
              </div>
            </div>
          ) : item ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name ชื่อ</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="surname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Surname นามสกุล</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone เบอร์โทรศัพท์มือถือ</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email อีเมล</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address ที่อยู่</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="property_bought"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>What property did you buy? / ซื้อโครงการอะไร</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="value_amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>How much was the value? / มูลค่าเท่าไหร่</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          inputMode="numeric"
                          value={new Intl.NumberFormat("en-US")
                            .format(Number(valueRaw || "0"))
                            ?.replace(/^0$/, "")}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, "");
                            setValueRaw(digits);
                            field.onChange(digits);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="developer"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Who was the developer? / ผู้พัฒนาโครงการคือใคร</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" disabled={saving} className="w-full">
                  {saving ? "Saving..." : "Save & Get Coupon"}
                </Button>
              </form>
            </Form>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
